'use client';

import { c as _c } from "react/compiler-runtime";
import { Stack, Typography } from '@mui/material';
import type { MPAPrincipal, MPARequest } from '@repo/api-gw-sdk';
import { MPAEntityType, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { BackupPolicyTag } from '@/components/backupPolicy/BackupPolicyTag';
import { CloudProviderCircle } from '@/components/cloudProviderIndicator';
import { Panels } from '@/components/panels/panels';
import { ClickableText } from '@/components/shared/clickableText';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';
import { PanelSize } from '@/contexts/workspaceContextTypes';
import { parseJson } from '@/utils/string';
import { getResourceIds } from './mpaResourceIds';
export const getBackupPolicyId = (request: MPARequest | null | undefined) => {
  const path = request?.requestPath;
  if (!path) {
    return null;
  }
  const match = /\/(?:backup-policies|policies)\/([^/?]+)/.exec(path);
  return match?.[1] ?? null;
};
export const getNotificationPolicyId = (request: MPARequest | null | undefined) => {
  const path = request?.requestPath;
  if (!path) {
    return null;
  }
  const match = /\/notification-policies\/([^/?]+)/.exec(path);
  return match?.[1] ?? null;
};
interface RestoreAccountInfo {
  cloudProvider?: Provider;
  accountId: string;
}
const getRestoreAccountInfo = (request: MPARequest | null | undefined): RestoreAccountInfo | null => {
  const body = parseJson<{
    restoreAccountAttributes?: {
      cloudProvider?: Provider;
      aws?: {
        roleArn?: string;
      };
      gcp?: {
        serviceAccount?: string;
      };
      azure?: {
        subscriptionId?: string;
      };
      mongoAtlas?: {
        atlasProjectId?: string;
      };
    };
  }>(request?.requestBody);
  const attrs = body?.restoreAccountAttributes;
  if (!attrs) {
    return null;
  }
  const cloudProvider = attrs.cloudProvider;
  switch (cloudProvider) {
    case Provider.Aws:
      {
        const roleArn = attrs.aws?.roleArn;
        const accountId = roleArn ? /arn:aws:iam::(\d+):/.exec(roleArn)?.[1] ?? roleArn : '';
        return accountId ? {
          cloudProvider,
          accountId
        } : null;
      }
    case Provider.Azure:
      {
        const accountId = attrs.azure?.subscriptionId ?? '';
        return accountId ? {
          cloudProvider,
          accountId
        } : null;
      }
    case Provider.Gcp:
      {
        const serviceAccount = attrs.gcp?.serviceAccount ?? '';
        const projectMatch = /@([^.]+)\.iam\.gserviceaccount\.com$/.exec(serviceAccount);
        const accountId = projectMatch?.[1] ?? serviceAccount;
        return accountId ? {
          cloudProvider,
          accountId
        } : null;
      }
    case Provider.MongoAtlas:
      {
        const accountId = attrs.mongoAtlas?.atlasProjectId ?? '';
        return accountId ? {
          cloudProvider,
          accountId
        } : null;
      }
    default:
      return null;
  }
};
export const getBackupPolicyNameFromState = (request: MPARequest | null | undefined) => {
  const state = request?.resourceStateBefore;
  if (!state) {
    return null;
  }
  return parseJson<{
    name?: string;
  }>(state)?.name ?? null;
};
export const getNotificationPolicyNameFromState = (request: MPARequest | null | undefined) => {
  const state = request?.resourceStateBefore;
  if (!state) {
    return null;
  }
  return parseJson<{
    name?: string;
  }>(state)?.name ?? null;
};
export const BackupPolicyEntity = ({
  policyId,
  fallbackName
}: {
  policyId: string | null;
  fallbackName?: string | null;
}) => {
  const dal = useDAL();
  const {
    data,
    isLoading
  } = dal.backupPolicy.useList();
  if (!policyId) {
    return null;
  }
  if (isLoading) {
    return <Typography variant='subtitle1' sx={{
      color: 'text.disabled'
    }}>
        Loading...
      </Typography>;
  }
  const policy = data?.backupPolicies?.find(p => p.id === policyId);
  if (!policy) {
    return <Typography variant='subtitle1' sx={{
      color: 'text.disabled'
    }}>
        {fallbackName ?? policyId}
      </Typography>;
  }
  return <BackupPolicyTag policy={policy} />;
};
export const NotificationPolicyEntity = t0 => {
  const $ = _c(5);
  const {
    policyId,
    name
  } = t0;
  const displayName = name ?? policyId;
  if (displayName === null) {
    return null;
  }
  let t1;
  if ($[0] !== name) {
    t1 = name === null || name === undefined ? {
      color: "text.disabled"
    } : undefined;
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== displayName || $[3] !== t1) {
    t2 = <Typography variant="subtitle1" sx={t1}>{displayName}</Typography>;
    $[2] = displayName;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const ResourceEntity = ({
  resourceId,
  clickable
}: {
  resourceId: string | null;
  clickable: boolean;
}) => {
  const dal = useDAL();
  const {
    rightPanel
  } = useWorkspace();
  const {
    data,
    isLoading
  } = dal.inventory.useResource(resourceId ?? '');
  if (!resourceId) {
    return null;
  }
  if (isLoading) {
    return <Typography variant='body2' sx={{
      color: 'text.disabled'
    }}>
        Loading...
      </Typography>;
  }
  const resource = data?.resource;
  const displayName = resource?.resourceName || resourceId;
  const openResourcePanel = () => {
    rightPanel.setIsOpen(true, PanelSize.Large);
    rightPanel.setComponent({
      panel: Panels.InventoryInstance,
      props: {
        id: resourceId,
        initialTab: 'overview',
        onClose: () => rightPanel.setIsOpen(false)
      }
    });
  };
  return <Stack direction='row' sx={{
    alignItems: 'center',
    gap: '8px'
  }}>
      {resource?.cloudProvider && <CloudProviderCircle cloudProvider={resource.cloudProvider} />}
      <OverflowTooltip text={displayName} renderContent={(text, ref) => <ClickableText ref={ref} data-testid='resource-name-cell' onClick={clickable ? openResourcePanel : undefined}>
            {text}
          </ClickableText>} />
    </Stack>;
};
const ResourceEntities = t0 => {
  const $ = _c(24);
  const {
    resourceIds,
    clickable: t1
  } = t0;
  const clickable = t1 === undefined ? true : t1;
  const [expanded, setExpanded] = useState(false);
  if (resourceIds.length <= 1) {
    const t2 = resourceIds[0] ?? null;
    let t3;
    if ($[0] !== clickable || $[1] !== t2) {
      t3 = <ResourceEntity resourceId={t2} clickable={clickable} />;
      $[0] = clickable;
      $[1] = t2;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    return t3;
  }
  let t2;
  if ($[3] !== expanded || $[4] !== resourceIds) {
    t2 = expanded ? resourceIds : resourceIds.slice(0, 5);
    $[3] = expanded;
    $[4] = resourceIds;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const shown = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      gap: "12px"
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = `${resourceIds.length} resources`;
  let t5;
  if ($[7] !== t4) {
    t5 = <Typography>{t4}</Typography>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      gap: "4px"
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== clickable || $[11] !== shown) {
    let t8;
    if ($[13] !== clickable) {
      t8 = id => <ResourceEntity key={id} resourceId={id} clickable={clickable} />;
      $[13] = clickable;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    t7 = shown.map(t8);
    $[10] = clickable;
    $[11] = shown;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = <Stack sx={t6}>{t7}</Stack>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== expanded || $[18] !== resourceIds.length) {
    t9 = resourceIds.length > 5 && <Typography className="underline hover:cursor-pointer" onClick={() => setExpanded(!expanded)}>{expanded ? "Show less" : `Show all ${resourceIds.length}`}</Typography>;
    $[17] = expanded;
    $[18] = resourceIds.length;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t5 || $[21] !== t8 || $[22] !== t9) {
    t10 = <Stack sx={t3}>{t5}{t8}{t9}</Stack>;
    $[20] = t5;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
};
const SnapshotResourceEntity = ({
  snapshotId
}: {
  snapshotId: string | null;
}) => {
  const dal = useDAL();
  const {
    data,
    isLoading
  } = dal.inventory.snapshots.useSnapshot(snapshotId ?? undefined);
  if (!snapshotId) {
    return null;
  }
  if (isLoading) {
    return <Typography variant='body2' sx={{
      color: 'text.disabled'
    }}>
        Loading...
      </Typography>;
  }
  const resourceId = data?.snapshot?.resourceId;
  if (!resourceId) {
    return <Typography variant='subtitle2'>{snapshotId}</Typography>;
  }
  return <ResourceEntity resourceId={resourceId} clickable={false} />;
};
const SnapshotResourceEntities = t0 => {
  const $ = _c(17);
  const {
    snapshotIds
  } = t0;
  const [expanded, setExpanded] = useState(false);
  if (snapshotIds.length <= 1) {
    const t1 = snapshotIds[0] ?? null;
    let t2;
    if ($[0] !== t1) {
      t2 = <SnapshotResourceEntity snapshotId={t1} />;
      $[0] = t1;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t1;
  if ($[2] !== expanded || $[3] !== snapshotIds) {
    t1 = expanded ? snapshotIds : snapshotIds.slice(0, 5);
    $[2] = expanded;
    $[3] = snapshotIds;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const shown = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      gap: "4px"
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = `${snapshotIds.length} resources`;
  let t4;
  if ($[6] !== t3) {
    t4 = <Typography variant="subtitle1Strong">{t3}</Typography>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== shown) {
    t5 = shown.map(_temp);
    $[8] = shown;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== expanded || $[11] !== snapshotIds.length) {
    t6 = snapshotIds.length > 5 && <Typography className="underline hover:cursor-pointer" onClick={() => setExpanded(!expanded)}>{expanded ? "Show less" : `Show all ${snapshotIds.length}`}</Typography>;
    $[10] = expanded;
    $[11] = snapshotIds.length;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <Stack sx={t2}>{t4}{t5}{t6}</Stack>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
export const MPARequestResourceEntities = t0 => {
  const $ = _c(6);
  const {
    request
  } = t0;
  let t1;
  if ($[0] !== request) {
    t1 = getResourceIds(request);
    $[0] = request;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const ids = t1;
  if (request?.entityType === MPAEntityType.MPA_ENTITY_TYPE_SNAPSHOT) {
    let t2;
    if ($[2] !== ids) {
      t2 = <SnapshotResourceEntities snapshotIds={ids} />;
      $[2] = ids;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t2;
  if ($[4] !== ids) {
    t2 = <ResourceEntities resourceIds={ids} />;
    $[4] = ids;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const saasEntityLabels: Partial<Record<MPAEntityType, string>> = {
  [MPAEntityType.MPA_ENTITY_TYPE_SAAS_USER_MAILBOX]: 'mailbox',
  [MPAEntityType.MPA_ENTITY_TYPE_SAAS_USER_DRIVE]: 'drive',
  [MPAEntityType.MPA_ENTITY_TYPE_SAAS_GROUP]: 'group',
  [MPAEntityType.MPA_ENTITY_TYPE_SAAS_SHARED_DRIVE]: 'shared drive',
  [MPAEntityType.MPA_ENTITY_TYPE_SAAS_SITE]: 'site',
  [MPAEntityType.MPA_ENTITY_TYPE_SAAS_USER]: 'user',
  [MPAEntityType.MPA_ENTITY_TYPE_SAAS_TEAM]: 'team'
};
const principalText = (principal: MPAPrincipal) => {
  const label = saasEntityLabels[principal.entityType];
  return label ? `${principal.id} ${label}` : principal.id;
};
export const MPASaasPrincipals = t0 => {
  const $ = _c(11);
  const {
    request
  } = t0;
  const source = request?.sourcePrincipal;
  const target = request?.targetPrincipal;
  if (!source) {
    return null;
  }
  const isInPlace = !target || target.id === source.id && target.entityType === source.entityType;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      alignItems: "center",
      gap: "8px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== source) {
    t2 = principalText(source);
    $[1] = source;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <Typography variant="subtitle2">{t2}</Typography>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== isInPlace || $[6] !== target) {
    t4 = !isInPlace && <><Typography variant="body2">into</Typography><Typography variant="subtitle2">{principalText(target)}</Typography></>;
    $[5] = isInPlace;
    $[6] = target;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <Stack direction="row" sx={t1}>{t3}{t4}</Stack>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
export const RestoreAccountEntity = t0 => {
  const $ = _c(10);
  const {
    request
  } = t0;
  let t1;
  if ($[0] !== request) {
    t1 = getRestoreAccountInfo(request);
    $[0] = request;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const info = t1;
  if (!info) {
    return null;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      alignItems: "center",
      gap: "8px"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== info.cloudProvider) {
    t3 = info.cloudProvider && <CloudProviderCircle cloudProvider={info.cloudProvider} />;
    $[3] = info.cloudProvider;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== info.accountId) {
    t4 = <Typography variant="subtitle2">{info.accountId}</Typography>;
    $[5] = info.accountId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <Stack direction="row" sx={t2}>{t3}{t4}</Stack>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
function _temp(id) {
  return <SnapshotResourceEntity key={id} snapshotId={id} />;
}